import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {useMutation, useQueryClient} from '@tanstack/react-query';

import {documentKeys} from '@/features/documents/hooks';
import {useApiClient} from '@/hooks';

import {buyersApi} from '../api';
import {assertBuyersReturned, invalidateBuyerCaches} from '../utils';

import type {CreateBuyersPayload} from '@sollapay/types';

export function useCreateBuyerMutation(trustAccountId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateBuyersPayload) => {
      const response = await buyersApi.createBuyers(client, trustAccountId, payload);
      assertBuyersReturned(response.data);
      return response;
    },
    // On failure too — a failed request may still have changed the roster server-side.
    onSettled: response => {
      invalidateBuyerCaches(
        queryClient,
        trustAccountId,
        response?.data.map(buyer => buyer.id) ?? []
      );
    },
    onSuccess: () => {
      // Remove (not just invalidate) buyer documents cache so the next modal open
      // starts with a clean fetch — invalidate alone serves stale data on remount.
      queryClient.removeQueries({
        queryKey: documentKeys.list(
          DocumentEntityType.TRUST_ACCOUNT,
          trustAccountId,
          DocumentContext.PURCHASE_DOCUMENTS
        )
      });
    }
  });
}
